import api from './ApiInstance';
import ApiRoutes from './ApiRoutes';

export interface ProcessedResult {
  searchQuery: string;
  response: string;
  threadId?: string; // Thread ID returned by backend
}

export interface QueryHistory {
  id: string;
  query: string;
  result: ProcessedResult;
  filters: {
    facilityNames?: string[];
    classificationNames?: string[];
    classificationTypes?: string[];
    dateRange?: {
      start: string;
      end: string;
    };
  };
  createdDate: string;
  createdBy: string;
}

// Use the API routes from ApiRoutes.js
const API_ENDPOINT = ApiRoutes.azureOpenAI;
const SAVE_QUERY_HISTORY_ENDPOINT = ApiRoutes.saveQueryHistory;
const GET_QUERY_HISTORY_ENDPOINT = ApiRoutes.getQueryHistory;
const GET_QUERY_HISTORY_ITEM_ENDPOINT = ApiRoutes.getQueryHistoryItem;
const DELETE_QUERY_HISTORY_ENDPOINT = ApiRoutes.deleteQueryHistory;

/**
 * Process a question with the Azure OpenAI service
 *
 * Backend Response Formats Supported:
 * 1. Simple string: "AI response text"
 * 2. Structured object: { response: "AI response text", threadId: "new_thread_id" }
 *
 * If backend returns a threadId, it will be used for subsequent requests
 */
export async function processQuestion(
  query: string,
  threadId?: string,
  facilityNames?: string[],
  classificationNames?: string[],
  classificationTypes?: string[],
  startDate?: string,
  endDate?: string
): Promise<ProcessedResult> {
  try {
    console.log('Processing question:', query);

    const payload = {
      query,
      filters: {
        facilityNames: facilityNames || [],
        classificationNames: classificationNames || [],
        classificationTypes: classificationTypes || [],
        startDate: startDate || '',
        endDate: endDate || '',
        threadId: threadId || ''
      }
    };
    
    // Make the API request
    const response = await api.post<typeof payload, any>(API_ENDPOINT, payload);
    console.log('Response received successfully');
    console.log('Response', response);

    // Handle different response formats
    let responseText: string;
    let returnedThreadId: string | undefined;

    if (typeof response === 'string') {
      // Simple string response
      responseText = response;
    } else if (response && typeof response === 'object') {
      // Structured response with potential thread ID
      responseText = response.response || response.text || JSON.stringify(response);
      returnedThreadId = response.threadId;
    } else {
      responseText = 'No response received from the server.';
    }

    // Create the processed result
    const result: ProcessedResult = {
      searchQuery: query,
      response: responseText,
      ...(returnedThreadId && { threadId: returnedThreadId })
    };
    
    // Only save to database if there is a valid response
    if (response && response !== 'No relevant incidents found for your query') {
      // Save to server
      try {
        await saveQueryToHistory(
          query,
          result,
          facilityNames,
          classificationNames,
          classificationTypes,
          startDate,
          endDate
        );
      } catch (saveError) {
        console.error('Failed to save query to server:', saveError);
        // Continue execution even if server save fails
      }

      // Save to localStorage as a fallback
      saveQueryToLocalStorage(
        query,
        result,
        facilityNames,
        classificationNames,
        classificationTypes,
        startDate,
        endDate
      );
    } else {
      console.log('No response received, skipping query history save');
    }
    
    // Return the processed result
    return result;
  } catch (error) {
    console.error('Error processing question:', error);
    
    const errorResult = {
      searchQuery: query,
      response: `Error processing question: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
    
    // Try to save errors to server too for a complete history
    // try {
    //   await saveQueryToHistory(
    //     query,
    //     errorResult
    //   );
    // } catch (saveError) {
    //   console.error('Failed to save error query to server:', saveError);
    //   // Continue execution even if server save fails
    // }

    // Always save errors to localStorage too for a complete history
    // saveQueryToLocalStorage(
    //   query,
    //   errorResult
    // );
    
    return errorResult;
  }
}

/**
 * Saves a query and its result to the history
 */
export async function saveQueryToHistory(
  query: string,
  result: ProcessedResult,
  facilityNames?: string[],
  classificationNames?: string[],
  classificationTypes?: string[],
  startDate?: string,
  endDate?: string
): Promise<void> {
  try {
    const payload = {
      query,
      result,
      filters: {
        facilityNames,
        classificationNames,
        classificationTypes,
        dateRange: {
          start: startDate,
          end: endDate
        }
      }
    };
    
    await api.post<typeof payload, void>(SAVE_QUERY_HISTORY_ENDPOINT, payload);
    console.log('Query saved to history successfully');
  } catch (error) {
    console.error('Failed to save query history:', error);
    throw error;
  }
}

/**
 * Gets the query history for the current user
 */
export async function getQueryHistory(): Promise<QueryHistory[]> {
  try {
    return await api.get<null, QueryHistory[]>(GET_QUERY_HISTORY_ENDPOINT);
  } catch (error) {
    console.error('Failed to fetch query history:', error);
    throw error;
  }
}

/**
 * Gets a specific query history item by ID
 */
export async function getQueryHistoryItem(id: string): Promise<QueryHistory> {
  try {
    return await api.get<null, QueryHistory>(`${GET_QUERY_HISTORY_ITEM_ENDPOINT}/${id}`);
  } catch (error) {
    console.error(`Failed to fetch query history item ${id}:`, error);
    throw error;
  }
}

/**
 * Save query to localStorage as a fallback
 */
export function saveQueryToLocalStorage(
  query: string,
  result: ProcessedResult,
  facilityNames?: string[],
  classificationNames?: string[],
  classificationTypes?: string[],
  startDate?: string,
  endDate?: string
): void {
  try {
    const history = JSON.parse(localStorage.getItem('queryHistory') || '[]');

    history.push({
      id: Date.now().toString(),
      query,
      result,
      filters: {
        facilityNames,
        classificationNames,
        classificationTypes,
        dateRange: {
          start: startDate,
          end: endDate
        }
      },
      createdDate: new Date().toISOString(),
      createdBy: localStorage.getItem('currentUserEmail') || ''
    });
    
    // Keep only the most recent 50 items
    if (history.length > 50) {
      history.splice(0, history.length - 50);
    }
    
    localStorage.setItem('queryHistory', JSON.stringify(history));
  } catch (error) {
    console.error('Failed to save to localStorage:', error);
  }
}

/**
 * Get query history from localStorage
 */
export function getLocalQueryHistory(): QueryHistory[] {
  try {
    return JSON.parse(localStorage.getItem('queryHistory') || '[]');
  } catch (error) {
    console.error('Failed to get from localStorage:', error);
    return [];
  }
}

/**
 * Get a specific query history item from localStorage by ID
 */
export function getLocalQueryHistoryItem(id: string): QueryHistory | null {
  try {
    const history = JSON.parse(localStorage.getItem('queryHistory') || '[]');
    return history.find((item: QueryHistory) => item.id === id) || null;
  } catch (error) {
    console.error(`Failed to get query history item ${id} from localStorage:`, error);
    return null;
  }
}

/**
 * Deletes a query history item by ID from the server
 */
export async function deleteQueryHistory(id: string): Promise<void> {
  try {
    await api.del<null, void>(`${DELETE_QUERY_HISTORY_ENDPOINT}/${id}`);
    console.log(`Query history item ${id} deleted successfully from server`);
    
    // Also delete from localStorage to keep both in sync
    deleteLocalQueryHistoryItem(id);
  } catch (error) {
    console.error(`Failed to delete query history item ${id}:`, error);
    throw error;
  }
}

/**
 * Deletes a query history item from localStorage by ID
 */
export function deleteLocalQueryHistoryItem(id: string): void {
  try {
    const history = JSON.parse(localStorage.getItem('queryHistory') || '[]');
    const filteredHistory = history.filter((item: QueryHistory) => item.id !== id);
    localStorage.setItem('queryHistory', JSON.stringify(filteredHistory));
    console.log(`Query history item ${id} deleted from localStorage`);
  } catch (error) {
    console.error(`Failed to delete query history item ${id} from localStorage:`, error);
  }
}
